import React, { useEffect, useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    TextField,
    IconButton,
    Typography, Checkbox, Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import api from '../../api';

const EditSetQuestionsModal = ({
    open,
    onClose,
    questionSetId,
    selectedExam,
    onSave }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [rowSelection, setRowSelection] = useState({});
    const [loading, setLoading] = useState(true);
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState([]);
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    const [questionLimit, setQuestionLimit] = useState(0); // or get from props
    const [searchText, setSearchText] = useState('');
    const [questions, setQuestions] = useState([]);


    // Fetch all questions and selected ones for this set
    // useEffect(() => {
    //     const fetchData = async () => {
    //         try {
    //             if (!questionSetId || !selectedExam) return;

    //             const allExamQuestionsRes = await axios.get(`${API_URL}/api/questions?exam_id=${selectedExam}`);
    //             const allExamQuestions = allExamQuestionsRes.data;
    //             console.log('✅ All Exam Questions:', allExamQuestions);

    //             const selectedSetRes = await axios.get(`${API_URL}/api/practice-set/${questionSetId}/questions`);
    //             console.log('Selected Set Response:', selectedSetRes.data);
    //             const selectedQuestionIds = new Set(selectedSetRes.data.data.map((q) => q.id));

    //             const questionsWithSelection = allExamQuestions.map((q) => ({
    //                 ...q,
    //                 is_selected: selectedQuestionIds.has(q.id),
    //             }));

    //             setQuestions(questionsWithSelection);
    //         } catch (error) {
    //             console.error('❌ Error fetching data:', error);
    //         } finally {
    //             setLoading(false); //  FIX: stop loading spinner
    //         }
    //     };

    //     if (open && questionSetId && selectedExam) {
    //         setLoading(true); //  ensure loading starts
    //         fetchData();
    //     }
    // }, [open, questionSetId, selectedExam]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!questionSetId || !selectedExam) return;

                //const allExamQuestionsRes = await axios.get(`${API_URL}/api/questions?exam_id=${selectedExam}`);
                const allExamQuestionsRes = await api.get(`/questions?exam_id=${selectedExam}`);
                const allExamQuestions = allExamQuestionsRes.data;

                //const selectedSetRes = await axios.get(`${API_URL}/api/practice-set/${questionSetId}/questions`);
                const selectedSetRes = await api.get(`/practice-set/${questionSetId}/questions`);
                const selectedQuestionIdsArray = selectedSetRes.data.data.map((q) => q.id);

                // Convert to { '1': true, '5': true } format for TanStack table
                const selectedRowMap = {};
                selectedQuestionIdsArray.forEach((id) => {
                    selectedRowMap[id] = true;
                });

                setQuestions(allExamQuestions);
                setRowSelection(selectedRowMap); // ✅ THIS preselects checkboxes
            } catch (error) {
                console.error('❌ Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (open && questionSetId && selectedExam) {
            setLoading(true);
            fetchData();
        }
    }, [open, questionSetId, selectedExam]);




    const SortableHeader = ({ column, title }) => (
        <Box
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={column.getToggleSortingHandler()}
        >
            {title}
            {column.getIsSorted() === 'asc' ? ' 🔼' : column.getIsSorted() === 'desc' ? ' 🔽' : ''}
        </Box>
    );

    // Define table columns
    const columns = useMemo(() => [
        {
            id: 'select',
            header: ({ table }) => (
                <Checkbox
                    checked={table.getIsAllRowsSelected()}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                />
            ),
            cell: ({ row, table }) => {
                const selected = row.getIsSelected();
                const selectedCount = Object.keys(table.getState().rowSelection).length;

                return (
                    <Checkbox
                        checked={selected}
                        onChange={(e) => {
                            if (!selected && questionLimit && selectedCount >= questionLimit) {
                                alert(`You can only select up to ${questionLimit} questions.`);
                                return;
                            }
                            row.getToggleSelectedHandler()(e);
                        }}
                    />
                );
            },
        },
        {
            accessorKey: 'id',
            header: ({ column }) => <SortableHeader column={column} title="ID" />,
            filterFn: 'includesString',
        },
        {
            accessorKey: 'question_text',
            header: ({ column }) => <SortableHeader column={column} title="Question" />,
            filterFn: 'includesString',
        },
        {
            accessorKey: 'option_a',
            header: ({ column }) => <SortableHeader column={column} title="Option A" />,
            filterFn: 'includesString',
        },
        {
            accessorKey: 'option_b',
            header: ({ column }) => <SortableHeader column={column} title="Option B" />,
            filterFn: 'includesString',
        },
        {
            accessorKey: 'option_c',
            header: ({ column }) => <SortableHeader column={column} title="Option C" />,
            filterFn: 'includesString',
        },
        {
            accessorKey: 'option_d',
            header: ({ column }) => <SortableHeader column={column} title="Option D" />,
            filterFn: 'includesString',
        },
        {
            accessorKey: 'correct_option',
            header: ({ column }) => <SortableHeader column={column} title="Correct" />,
            filterFn: 'includesString',
        },
    ], [questionLimit]);

    // Table instance
    const table = useReactTable({
        data: questions,
        columns,
        state: {
            globalFilter: searchText,
            rowSelection,
        },
        onRowSelectionChange: setRowSelection,
        getRowId: (row) => row.id,
        enableRowSelection: true,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        globalFilterFn: 'includesString',
    });

    // Save selected questions
    const handleSave = async () => {
        const selectedIds = table
            .getSelectedRowModel()
            .rows
            .map(row => row.original.id);

        try {
            setLoading(true);  // Start spinner and disable button

            await api.post(`/practice-set/${questionSetId}/update-questions`, { question_ids: selectedIds });

            Swal.fire({
                icon: 'success',
                title: 'Updated!',
                text: 'Practice set questions updated successfully.',
                timer: 2000,
                showConfirmButton: false,
            });

            onSave();
            onClose();
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: 'Failed to save changes. Please try again.',
            });
            console.error('Failed to save changes:', err);
        } finally {
            setLoading(false);  // Stop spinner and enable button
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            fullScreen={fullScreen}
        >
            <DialogTitle
                sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    style={{
                        fontWeight: 'bold',
                        fontSize: '1.5rem',
                        color: '#1976d2',
                        fontFamily: 'Roboto, sans-serif'
                    }}
                >
                    ✏️ Edit Question Selection
                </motion.div>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8, color: 'red' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                        <CircularProgress />
                    </div>
                ) : questions.length === 0 ? (
                    <Typography variant="body1" sx={{ textAlign: 'center', mt: 4 }}>
                        No questions available for this set.
                    </Typography>
                ) : (
                    <>
                        <TextField
                            fullWidth
                            placeholder="Search..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}

                            sx={{ mb: 2 }}
                        />

                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <tr key={headerGroup.id} style={{ backgroundColor: '#f0f0f0' }}>
                                            {headerGroup.headers.map((header) => (
                                                <th
                                                    key={header.id}
                                                    style={{
                                                        border: '1px solid #ccc',
                                                        padding: '8px',
                                                        textAlign: 'left',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.map((row, index) => (
                                        <tr
                                            key={row.id}
                                            style={{
                                                backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa',
                                                transition: 'background-color 0.2s',
                                            }}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <td
                                                    key={cell.id}
                                                    style={{ border: '1px solid #eee', padding: '8px' }}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <Button
                                onClick={() => table.setPageIndex(0)}
                                disabled={table.getState().pagination.pageIndex === 0}
                            >
                                First
                            </Button>

                            <Button
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}
                            >
                                Previous
                            </Button>

                            <Typography variant="body1" sx={{ alignSelf: 'center' }}>
                                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                            </Typography>

                            <TextField
                                type="number"
                                label="Go to page"
                                variant="outlined"
                                size="small"
                                inputProps={{
                                    min: 1,
                                    max: table.getPageCount(),
                                }}
                                defaultValue={table.getState().pagination.pageIndex + 1}
                                onChange={(e) => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                    table.setPageIndex(page);
                                }}
                                sx={{
                                    width: 100,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                    },
                                }}
                            />


                            <Button
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}
                            >
                                Next
                            </Button>

                            <Button
                                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                disabled={table.getState().pagination.pageIndex === table.getPageCount() - 1}
                            >
                                Last
                            </Button>
                        </div>

                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="error" variant="outlined">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditSetQuestionsModal;

